import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { isContactImportJobTerminalStatus, type ContactImportJobDto } from "@multi-chat/shared";
import { getActiveContactImportJob, getContactImportJob } from "../../api/contacts-api.js";
import { contactLimitFeedback } from "../contacts/contact-errors.js";
import { NOTIFICATION_DURATION_MS } from "./notification-types.js";
import { useNotifications } from "./notification-context.js";
import {
  CONTACT_IMPORT_JOB_POLL_MS,
  CONTACT_IMPORT_WATCH_EVENT,
  decideContactImportPoll,
  type ContactImportWatchDetail,
} from "../contacts/contact-import-polling.js";

export function ContactImportJobPoller({
  organizationId,
}: {
  organizationId: number;
}): null {
  const { t } = useTranslation();
  const { upsertImportNotification, clearImportNotification } = useNotifications();
  const seenTerminal = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;
    let watchedJobId: string | null = null;

    function stopPolling(): void {
      if (timer != null) {
        window.clearInterval(timer);
        timer = undefined;
      }
      watchedJobId = null;
    }

    function toastMessage(job: ContactImportJobDto): { message: string; detail?: string } {
      if (job.status === "COMPLETED") {
        return {
          message: t("contacts.import.job.completedToast", {
            created: job.createdCount,
            restored: job.restoredCount,
          }),
        };
      }
      if (job.status === "FAILED" && job.errorCode === "CONTACT_LIMIT_REACHED") {
        const copy = contactLimitFeedback(t, "import");
        return { message: copy.title, detail: copy.message };
      }
      if (job.status === "FAILED") return { message: t("contacts.import.job.failedToast") };
      if (job.status === "CANCELLED") return { message: t("contacts.import.job.cancelledToast") };
      return { message: t("contacts.import.job.progressToast") };
    }

    function applyJob(job: ContactImportJobDto): void {
      const decision = decideContactImportPoll(job);
      const terminal = isContactImportJobTerminalStatus(job.status);
      if (!decision.showToast && !terminal) return;
      if (terminal && seenTerminal.current === job.id) return;
      if (terminal) seenTerminal.current = job.id;

      const toast = toastMessage(job);
      upsertImportNotification({
        jobId: job.id,
        message: toast.message,
        detail: toast.detail,
        durationMs: job.status === "COMPLETED" ? NOTIFICATION_DURATION_MS : null,
        processedRows: job.processedRows,
        totalRows: job.totalRows,
        createdCount: job.createdCount,
        restoredCount: job.restoredCount,
        skippedCount: job.skippedCount,
        failedCount: job.failedCount,
        status: job.status,
      });
      if (job.status === "COMPLETED" || job.status === "CANCELLED") {
        window.dispatchEvent(new Event("contact-import-updated"));
      }
    }

    function startJobPolling(jobId: string): void {
      stopPolling();
      watchedJobId = jobId;
      timer = window.setInterval(() => {
        void (async () => {
          if (!watchedJobId) return;
          try {
            const latest = await getContactImportJob(organizationId, watchedJobId);
            if (cancelled) return;
            applyJob(latest);
            if (decideContactImportPoll(latest).stop) {
              stopPolling();
            }
          } catch {
            /* keep last toast; next poll retries */
          }
        })();
      }, CONTACT_IMPORT_JOB_POLL_MS);
    }

    async function recover(): Promise<void> {
      try {
        const { job } = await getActiveContactImportJob(organizationId);
        if (cancelled || !job) return;
        const decision = decideContactImportPoll(job);
        if (decision.showToast) applyJob(job);
        if (decision.pollJob) startJobPolling(job.id);
      } catch {
        /* no sticky toast until a later watch/recovery */
      }
    }

    function onWatch(event: Event): void {
      const jobId = (event as CustomEvent<ContactImportWatchDetail>).detail?.jobId;
      if (!jobId || cancelled) return;
      startJobPolling(jobId);
      void (async () => {
        try {
          const latest = await getContactImportJob(organizationId, jobId);
          if (cancelled) return;
          applyJob(latest);
        } catch {
          /* interval will retry */
        }
      })();
    }

    void recover();
    window.addEventListener(CONTACT_IMPORT_WATCH_EVENT, onWatch);

    return () => {
      cancelled = true;
      stopPolling();
      window.removeEventListener(CONTACT_IMPORT_WATCH_EVENT, onWatch);
      void clearImportNotification;
    };
  }, [organizationId, t, upsertImportNotification, clearImportNotification]);

  return null;
}
